// Real-time voice analysis for "Mimic" mode: pitch (autocorrelation) + F1/F2 (LPC envelope peaks).
import { acquireMic, idleMic } from '../samuel/mic';
const LPC_ORDER = 12;

const autocorr = (x, lag) => { let s = 0; for (let i = lag; i < x.length; i++) s += x[i] * x[i - lag]; return s; };

const levinson = (r, order) => {
  const a = new Float64Array(order + 1); a[0] = 1;
  let err = r[0];
  for (let i = 1; i <= order; i++) {
    let acc = r[i];
    for (let j = 1; j < i; j++) acc += a[j] * r[i - j];
    const k = -acc / err;
    const prev = a.slice();
    for (let j = 1; j < i; j++) a[j] = prev[j] + k * prev[i - j];
    a[i] = k;
    err *= 1 - k * k;
    if (err <= 0) break;
  }
  return a;
};

const pitchOf = (buf, sr) => {
  const minLag = Math.floor(sr / 400), maxLag = Math.floor(sr / 70);
  const r0 = autocorr(buf, 0);
  if (r0 <= 0) return 0;
  let best = 0, bestLag = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    const v = autocorr(buf, lag) / r0;
    if (v > best) { best = v; bestLag = lag; }
  }
  return best > 0.45 ? sr / bestLag : 0;
};

export const formantsOf = (buf, sr) => {
  const step = Math.max(1, Math.round(sr / 11025));
  const fs = sr / step;
  const n = Math.floor(buf.length / step);
  const x = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const cur = buf[i * step], prev = i ? buf[(i - 1) * step] : 0;
    x[i] = (cur - 0.94 * prev) * (0.54 - 0.46 * Math.cos((2 * Math.PI * i) / (n - 1)));
  }
  const r = new Float64Array(LPC_ORDER + 1);
  for (let k = 0; k <= LPC_ORDER; k++) r[k] = autocorr(x, k);
  if (r[0] <= 0) return null;
  const a = levinson(r, LPC_ORDER);
  const bins = 256, peaks = [];
  let prev2 = 0, prev1 = 0;
  for (let b = 0; b < bins; b++) {
    const w = (Math.PI * b) / bins;
    let re = 0, im = 0;
    for (let k = 0; k <= LPC_ORDER; k++) { re += a[k] * Math.cos(k * w); im -= a[k] * Math.sin(k * w); }
    const mag = 1 / (re * re + im * im);
    if (b > 1 && prev1 > prev2 && prev1 > mag) peaks.push(((b - 1) * fs) / (2 * bins));
    prev2 = prev1; prev1 = mag;
  }
  const f = peaks.filter((p) => p > 200 && p < 3500);
  if (f.length < 2) return null;
  return { f1: f[0], f2: f[1] };
};

// F1 ↔ openness (td), F2 ↔ frontness (ti). Ranges tuned on adult speakers.
export const formantsToTongue = ({ f1, f2 }) => {
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  return {
    td: clamp(2.05 + ((f1 - 270) / (850 - 270)) * 1.4, 2.05, 3.45),
    ti: clamp(12.5 + ((f2 - 800) / (2400 - 800)) * 16.5, 12.5, 29),
  };
};

export const startVoiceTracker = async (onFrame) => {
  const { ctx, src } = await acquireMic();
  const an = ctx.createAnalyser();
  an.fftSize = 2048;
  src.connect(an);
  const buf = new Float32Array(an.fftSize);
  let raf = 0, last = 0;
  const loop = (now) => {
    raf = requestAnimationFrame(loop);
    if (now - last < 40) return;
    last = now;
    an.getFloatTimeDomainData(buf);
    let rms = 0; for (let i = 0; i < buf.length; i++) rms += buf[i] * buf[i];
    rms = Math.sqrt(rms / buf.length);
    if (rms < 0.012) { onFrame({ rms, voiced: false }); return; }
    const f0 = pitchOf(buf, ctx.sampleRate);
    const fm = f0 ? formantsOf(buf, ctx.sampleRate) : null;
    onFrame({ rms, voiced: !!f0, f0, ...(fm || {}) });
  };
  raf = requestAnimationFrame(loop);
  return () => { cancelAnimationFrame(raf); try { src.disconnect(an); } catch { /* released */ } idleMic(); };
};
